import React, { Component, ErrorInfo, ReactNode } from 'react';
import { NeonButton } from './ui/NeonButton';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Report to Sentry
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });

    this.setState({
      error,
      errorInfo
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
          <div className="max-w-md w-full bg-gray-800/50 backdrop-blur-sm border border-red-500/20 rounded-lg p-6 space-y-4">
            <div className="text-center">
              <div className="text-red-500 text-5xl mb-4">⚠️</div>
              <h2 className="text-2xl font-bold text-white mb-2">Something went wrong</h2>
              <p className="text-gray-400 text-sm mb-4">
                We encountered an unexpected error. Please try again.
              </p>
            </div>

            {this.state.error && (
              <div className="bg-gray-900/50 border border-red-500/20 rounded p-3">
                <p className="text-red-400 text-xs font-mono break-all">
                  {this.state.error.toString()}
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <NeonButton
                onClick={this.handleReset}
                className="flex-1"
              >
                Try Again
              </NeonButton>
              <NeonButton
                onClick={() => window.location.href = '/'}
                className="flex-1"
              >
                Go Home
              </NeonButton>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
